// What agents are told of a change's complexity (relayTools' files and
// diff): the open Files view's own figures for a file it has computed,
// so an agent and the person read the same numbers, else the engine's
// estimate from GitHub's patch alone (loose only: no whole text is read).
// docs/superpowers/specs/2026-10-31-change-complexity-design.md.
import type { Block, Row } from '../engine/types';
import { matchKey, subjectKey } from '../api/relaySubject';
import type { Subject } from '../subject';
import { eng } from '../diff/engine';
import { effectiveComplexity } from '../logic/complexity';

/** One change block's complexity, as an agent reads it. Line ranges are its rows' on each side (a hunk's). */
export interface AgentBlock {
  old_start: number;
  old_lines: number;
  new_start: number;
  new_lines: number;
  loose: number;
  tight: number | null;
  score: number;
  band: string;
  reasons: string[];
  flags: string[];
}

/**
 * A file's complexity, as an agent reads it: `view` when the person's open
 * Files view scored it (both figures, moves discounted), `patch` when only
 * GitHub's patch was (the estimate alone). `think` sums the blocks' scores.
 */
export interface AgentFileComplexity {
  basis: 'view' | 'patch';
  think: number;
  blocks: AgentBlock[];
}

/** A view's answer for a file at `path` whose new side is the blob `sha` (null: deleted); null when it has no figures for that version. */
export type ComplexityProvider = (path: string, sha: string | null) => AgentFileComplexity | null;

const providers = new Map<string, ComplexityProvider>();
/** One key per subject, whatever the case of its owner and repo (relaySubject.ts `matchKey`). */
const keyOf = (subject: Subject) => matchKey(subjectKey(subject));

/** Offers a Files view's figures for `subject` to agents; the returned function withdraws them (only if still this view's). */
export function provideComplexity(subject: Subject, provider: ComplexityProvider): () => void {
  const key = keyOf(subject);
  providers.set(key, provider);
  return () => {
    if (providers.get(key) === provider) providers.delete(key);
  };
}

/** The open view's figures for `path` at blob `sha`, if a view of `subject` has them. */
export function viewComplexity(subject: Subject, path: string, sha: string | null): AgentFileComplexity | null {
  return providers.get(keyOf(subject))?.(path, sha) ?? null;
}

/** A side's range over `rows`: the first line number and how many rows carry one. */
function range(rows: readonly Row[], side: 'old' | 'new'): [number, number] {
  const lines = rows.map((r) => r[side]).filter((n): n is number => n !== null);
  return lines.length ? [Math.min(...lines), lines.length] : [0, 0];
}

/** `blocks`' change blocks as agents read them, each scored after the rows `isMoved` says moved. */
export function agentBlocks(blocks: readonly Block[], isMoved: (row: Row) => boolean): AgentBlock[] {
  const out: AgentBlock[] = [];
  for (const b of blocks) {
    if (b.kind !== 'change') continue;
    const c = effectiveComplexity(b, isMoved);
    if (!c) continue;
    const [oldStart, oldLines] = range(b.rows, 'old');
    const [newStart, newLines] = range(b.rows, 'new');
    out.push({ old_start: oldStart, old_lines: oldLines, new_start: newStart, new_lines: newLines, loose: c.loose, tight: c.tight, score: c.score, band: c.band, reasons: c.reasons, flags: c.flags });
  }
  return out;
}

/**
 * A changed file's complexity for an agent: the open view's when it has
 * this version, else the engine's estimate from `patch`; null without
 * either (no patch, or the engine unavailable).
 */
export async function fileComplexity(subject: Subject, path: string, sha: string | null, patch: string | null): Promise<AgentFileComplexity | null> {
  const seen = viewComplexity(subject, path, sha);
  if (seen) return seen;
  if (!patch) return null;
  const language = await eng.detectLanguage(path).catch(() => null);
  const hunks = await eng.patchComplexity(path, language, patch).catch(() => []);
  if (!hunks.length) return null;
  const blocks: AgentBlock[] = hunks.map((h) => ({
    old_start: h.oldStart,
    old_lines: h.oldLines,
    new_start: h.newStart,
    new_lines: h.newLines,
    loose: h.complexity.loose,
    tight: null,
    score: h.complexity.score,
    band: h.complexity.band,
    reasons: h.complexity.reasons,
    flags: h.complexity.flags,
  }));
  return { basis: 'patch', think: blocks.reduce((n, b) => n + b.score, 0), blocks };
}
